import React, { Component } from "react";
import { AxiosError, AxiosResponse } from "axios";
import { notification } from "../../../../utils/helpers";
import { putRequest } from "../../../../utils/httpRequest";

interface Props {
  userId: number;
  showPasswordModal: boolean;
  handleShowPasswordModal: (val: boolean) => void;
}

interface State {
  errors: {
    password?: string;
    passwordConfirmation?: string;
  };
  password: string;
  passwordConfirmation: string;
  updatingPassword: boolean;
}

class Password extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      errors: {},
      password: "",
      passwordConfirmation: "",
      updatingPassword: false,
    };
  }

  closeModal = () => {
    this.setState({
      errors: {},
      password: "",
      passwordConfirmation: "",
      updatingPassword: false,
    });
    this.props.handleShowPasswordModal(false);
  };

  handleChange = (key, event) => {
    const { value } = event.target;

    this.setState(
      (state) =>
        ({
          [key]: value,
          errors: {
            ...state.errors,
            [key]: undefined,
          },
        }) as Pick<State, keyof State>,
    );
  };

  validatePassword = () => {
    const { password, passwordConfirmation } = this.state;
    const errors = {} as State["errors"];

    if (!password) {
      errors.password = "Password is required.";
    }

    if (!passwordConfirmation) {
      errors.passwordConfirmation = "Password confirmation is required.";
    } else if (password && password !== passwordConfirmation) {
      errors.passwordConfirmation = "Password confirmation must match.";
    }

    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  updatePassword = (event) => {
    const { userId } = this.props;
    const { password, passwordConfirmation } = this.state;

    event.preventDefault();

    if (!this.validatePassword()) {
      return;
    }

    this.setState({ updatingPassword: true });

    return putRequest(`/api/users/${userId}/change_password.json`, {
      user: {
        password,
        password_confirmation: passwordConfirmation,
      },
    })
      .then((_response: AxiosResponse) => {
        notification("Password was successfully updated.", "notification");
        this.closeModal();
      })
      .catch((error: AxiosError<{ error: string }>) => {
        notification(
          error.response?.data?.error || "Password update failed.",
          "error",
        );
        this.setState({ updatingPassword: false });
      });
  };

  render() {
    const { showPasswordModal } = this.props;
    const { errors, password, passwordConfirmation, updatingPassword } =
      this.state;

    return (
      <div
        className={`pw-backdrop ${showPasswordModal ? "is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pwTitle"
        aria-hidden={!showPasswordModal}
      >
        <form className="pw-modal" onSubmit={this.updatePassword}>
          <h2 id="pwTitle">Change password</h2>
          <p className="pw-sub">
            Choose something memorable but hard to guess. You'll stay signed in
            on this device.
          </p>
          <div className="set-field">
            <label htmlFor="pwNew">New password</label>
            <input
              className="set-input"
              id="pwNew"
              type="password"
              autoComplete="new-password"
              placeholder="New password"
              value={password}
              onChange={(event) => this.handleChange("password", event)}
            />
            {errors.password && (
              <div className="set-error">{errors.password}</div>
            )}
          </div>
          <div className="set-field">
            <label htmlFor="pwConfirm">Confirm new password</label>
            <input
              className="set-input"
              id="pwConfirm"
              type="password"
              autoComplete="new-password"
              placeholder="Confirm new password"
              value={passwordConfirmation}
              onChange={(event) =>
                this.handleChange("passwordConfirmation", event)
              }
            />
            {errors.passwordConfirmation && (
              <div className="set-error">{errors.passwordConfirmation}</div>
            )}
          </div>
          <div className="pw-foot">
            <button
              type="button"
              className="btn btn-ghost btn--sm"
              onClick={this.closeModal}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`btn btn-primary btn--sm ${
                updatingPassword ? "loading" : ""
              }`}
              disabled={updatingPassword}
            >
              Save password
            </button>
          </div>
        </form>
      </div>
    );
  }
}

export default Password;
